// Differences vs. QuestionChartsSection: Y-axis is "Themes", label field is
// `theme` (no "Q#: " prefix), total chart uses labelPosition="middle" + 10-char
// tooltip truncation, single-metric charts have no tooltip.

import React from "react";
import { Typography } from "@material-tailwind/react";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";
import {
    calculateLeftMargin,
    truncateLabel,
} from "../data-transforms";
import {
    ChartTablePair,
    COMMON_CHART_PROPS,
    buildEngagementLegend,
    makeChartTooltip,
    ResponsiveBar,
    CHART_BASE_MARGIN,
} from "./chart-table-pair";

export function ThemeChartsSection({
    themeEngagementData,
    themeByTotalEngagement,
    themeByTotalEngagementForChart,
    themeByContributors,
    themeByContributorsForChart,
    themeByResponses,
    themeByResponsesForChart,
    themeByUnique,
    themeByUniqueForChart,
    openChartTables,
    toggleChartTable,
    legendDirection,
    getLegendPosition,
    getBottomMargin,
    getBottomMarginNoLegend,
    getLegendItemWidth,
}) {
    const visible = themeEngagementData?.length > 0;
    const legendHelpers = { legendDirection, getLegendPosition, getLegendItemWidth };

    // Pre-compute label arrays used by calculateLeftMargin. Themes use
    // the theme name directly (no "Q#:" prefix) truncated to 30 chars
    // for the label, then re-truncated to 10 for tick format.
    const buildLabels = (data) =>
        data
            .slice(0, 10)
            .map((t) => `${truncateLabel(t.theme, 30)}`)
            .map((l) => truncateLabel(l, 10));
    const totalEngagementLabels = buildLabels(themeByTotalEngagementForChart);
    const contributorsLabels = buildLabels(themeByContributorsForChart);
    const responsesLabels = buildLabels(themeByResponsesForChart);
    const uniqueLabels = buildLabels(themeByUniqueForChart);

    return (
        <div className="mb-6">
            <Typography variant="h5" className="mb-1">
                Survey Theme Charts
            </Typography>

            {/* 1. Survey Engagement by Theme — full-width, 3-key */}
            <ChartTablePair
                title="Survey Engagement by Theme"
                visible={visible}
                yAxisLabel="Themes"
                chartHeight="600px"
                toggleKey="surveyThemeEngagement"
                openChartTables={openChartTables}
                toggleChartTable={toggleChartTable}
                className="mb-6"
                chart={
                    <ResponsiveBar
                        {...COMMON_CHART_PROPS}
                        data={themeByTotalEngagementForChart.slice(0, 10).map((t) => ({
                            label: `${truncateLabel(t.theme, 30)}`,
                            theme: t.theme,
                            contributors: t.contributors,
                            responses: t.responses,
                            uniqueResponses: t.uniqueResponses,
                        }))}
                        keys={["contributors", "responses", "uniqueResponses"]}
                        margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMargin(3), left: calculateLeftMargin(totalEngagementLabels, { charWidth: 5, padding: 15 }) }}
                        colors={["#fdc403", "#aaa9a9", "#c5c5c5"]}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: "Count",
                            legendPosition: "middle",
                            legendOffset: 40,
                        }}
                        labelPosition="middle"
                        legends={buildEngagementLegend(
                            ["contributors", "responses", "uniqueResponses"],
                            ["#fdc403", "#aaa9a9", "#c5c5c5"],
                            legendHelpers,
                        )}
                        tooltip={makeChartTooltip(10)}
                    />
                }
                table={
                    <GobbyTable>
                        <GobbyTableHead>
                            <GobbyTableRow>
                                <GobbyTableHeader>Theme</GobbyTableHeader>
                                <GobbyTableHeader>Contributors</GobbyTableHeader>
                                <GobbyTableHeader>Responses</GobbyTableHeader>
                                <GobbyTableHeader>Unique Responses</GobbyTableHeader>
                            </GobbyTableRow>
                        </GobbyTableHead>
                        <GobbyTableBody>
                            {themeByTotalEngagement.map((t, index) => (
                                <GobbyTableRow key={index}>
                                    <GobbyTableCell>{truncateLabel(t.theme, 40)}</GobbyTableCell>
                                    <GobbyTableCell>{t.contributors}</GobbyTableCell>
                                    <GobbyTableCell>{t.responses}</GobbyTableCell>
                                    <GobbyTableCell>{t.uniqueResponses}</GobbyTableCell>
                                </GobbyTableRow>
                            ))}
                        </GobbyTableBody>
                    </GobbyTable>
                }
            />

            {/* 2-4. Three single-metric charts in a 2-col grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">

                {/* 2. Survey Contributors by Theme — no tooltip on theme singles */}
                <ChartTablePair
                    title="Survey Contributors by Theme"
                    visible={visible}
                    yAxisLabel="Themes"
                    chartHeight="400px"
                    toggleKey="surveyThemeContributors"
                    openChartTables={openChartTables}
                    toggleChartTable={toggleChartTable}
                    chart={
                        <ResponsiveBar
                            {...COMMON_CHART_PROPS}
                            data={themeByContributorsForChart.slice(0, 10).map((t) => ({
                                label: `${truncateLabel(t.theme, 30)}`,
                                contributors: t.contributors,
                            }))}
                            keys={["contributors"]}
                            margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMarginNoLegend(), left: calculateLeftMargin(contributorsLabels, { charWidth: 5, padding: 15 }) }}
                            colors={["#fdc403"]}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "Contributors",
                                legendPosition: "middle",
                                legendOffset: 40,
                            }}
                            labelPosition="end"
                            labelOffset={4}
                        />
                    }
                    table={
                        <GobbyTable>
                            <GobbyTableHead>
                                <GobbyTableRow>
                                    <GobbyTableHeader>Theme</GobbyTableHeader>
                                    <GobbyTableHeader>Contributors</GobbyTableHeader>
                                </GobbyTableRow>
                            </GobbyTableHead>
                            <GobbyTableBody>
                                {themeByContributors.map((t, index) => (
                                    <GobbyTableRow key={index}>
                                        <GobbyTableCell>{truncateLabel(t.theme, 40)}</GobbyTableCell>
                                        <GobbyTableCell>{t.contributors}</GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                            </GobbyTableBody>
                        </GobbyTable>
                    }
                />

                {/* 3. Survey Responses by Theme — no tooltip on theme singles */}
                <ChartTablePair
                    title="Survey Responses by Theme"
                    visible={visible}
                    yAxisLabel="Themes"
                    chartHeight="400px"
                    toggleKey="surveyThemeResponses"
                    openChartTables={openChartTables}
                    toggleChartTable={toggleChartTable}
                    chart={
                        <ResponsiveBar
                            {...COMMON_CHART_PROPS}
                            data={themeByResponsesForChart.slice(0, 10).map((t) => ({
                                label: `${truncateLabel(t.theme, 30)}`,
                                responses: t.responses,
                            }))}
                            keys={["responses"]}
                            margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMarginNoLegend(), left: calculateLeftMargin(responsesLabels, { charWidth: 5, padding: 15 }) }}
                            colors={["#aaa9a9"]}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "Responses",
                                legendPosition: "middle",
                                legendOffset: 40,
                            }}
                            labelPosition="end"
                            labelOffset={4}
                        />
                    }
                    table={
                        <GobbyTable>
                            <GobbyTableHead>
                                <GobbyTableRow>
                                    <GobbyTableHeader>Theme</GobbyTableHeader>
                                    <GobbyTableHeader>Responses</GobbyTableHeader>
                                </GobbyTableRow>
                            </GobbyTableHead>
                            <GobbyTableBody>
                                {themeByResponses.map((t, index) => (
                                    <GobbyTableRow key={index}>
                                        <GobbyTableCell>{truncateLabel(t.theme, 40)}</GobbyTableCell>
                                        <GobbyTableCell>{t.responses}</GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                            </GobbyTableBody>
                        </GobbyTable>
                    }
                />

                {/* 4. Survey Unique Responses by Theme — no tooltip on theme singles */}
                <ChartTablePair
                    title="Survey Unique Responses by Theme"
                    visible={visible}
                    yAxisLabel="Themes"
                    chartHeight="400px"
                    toggleKey="surveyThemeUniqueResponses"
                    openChartTables={openChartTables}
                    toggleChartTable={toggleChartTable}
                    chart={
                        <ResponsiveBar
                            {...COMMON_CHART_PROPS}
                            data={themeByUniqueForChart.slice(0, 10).map((t) => ({
                                label: `${truncateLabel(t.theme, 30)}`,
                                uniqueResponses: t.uniqueResponses,
                            }))}
                            keys={["uniqueResponses"]}
                            margin={{ ...CHART_BASE_MARGIN, bottom: getBottomMarginNoLegend(), left: calculateLeftMargin(uniqueLabels, { charWidth: 5, padding: 15 }) }}
                            colors={["#c5c5c5"]}
                            axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: 0,
                                legend: "Unique Responses",
                                legendPosition: "middle",
                                legendOffset: 40,
                            }}
                            labelPosition="end"
                            labelOffset={4}
                        />
                    }
                    table={
                        <GobbyTable>
                            <GobbyTableHead>
                                <GobbyTableRow>
                                    <GobbyTableHeader>Theme</GobbyTableHeader>
                                    <GobbyTableHeader>Unique Responses</GobbyTableHeader>
                                </GobbyTableRow>
                            </GobbyTableHead>
                            <GobbyTableBody>
                                {themeByUnique.map((t, index) => (
                                    <GobbyTableRow key={index}>
                                        <GobbyTableCell>{truncateLabel(t.theme, 40)}</GobbyTableCell>
                                        <GobbyTableCell>{t.uniqueResponses}</GobbyTableCell>
                                    </GobbyTableRow>
                                ))}
                            </GobbyTableBody>
                        </GobbyTable>
                    }
                />
            </div>
        </div>
    );
}
